/**
 * MenuItem component.
 */
import Focus from '@shared/types/enums/focus';
import ItemContainer from '@components/atoms/container/menuItemContainer';
import IconContainer from '@components/atoms/container/menuItemIconContainer';
import ListContext from '@shared/components/utils/context/listContext';
import MenuContext from '@shared/components/utils/context/menuContext';
import TextContainer from '@components/atoms/container/menuItemTextContainer';
import TranslationManager from '@shared/assets/locale/translationsManager';
import type { MenuItem as MenuItemType } from '@shared/components/menu/menuData';
import { selectFocus } from '@shared/store/slices/focus.slice';
import { selectMagicRemoteEnabled } from '@shared/store/slices/magicRemote.slice';
import { useAppSelector } from '@shared/store/hooks';
import { useCallback, useContext, useMemo, useState } from 'react';
import {
  selectMenuCollapse,
  selectMenuDisplay,
} from '@shared/store/slices/menu.slice';

/**
 * Props for the menu item component.
 */
interface Props {
  focused?: boolean;
  index?: number;
  item: MenuItemType;
  onClick: (index?: number) => void;
}

/**
 * Displays a menu item with an icon and text.
 * @param parentProps - The props for the menu item component.
 * @returns {JSX.Element} - The menu item component.
 */
export default function MenuItem(parentProps: Props) {
  const { callbacks, props, text } = useMenuItem(parentProps);
  const { item } = parentProps;
  const { Icon } = item;
  return (
    <ItemContainer {...callbacks.itemContainer} {...props.itemContainer}>
      <IconContainer>
        {Icon ? <Icon {...props.iconElement} /> : null}
      </IconContainer>
      {text ? <TextContainer>{text}</TextContainer> : null}
    </ItemContainer>
  );
}

/**
 * Hook that handles the focused state of the menu item and the enter and focus actions.
 * @param parentProps - The props for the menu item component.
 * @returns {Object} - The props for the menu item components: container, icon, item.
 */
function useMenuItem(parentProps: Props) {
  // Selectors
  const collapse = useAppSelector(selectMenuCollapse);
  const display = useAppSelector(selectMenuDisplay);
  const focus = useAppSelector(selectFocus);
  const enabled = useAppSelector(selectMagicRemoteEnabled);

  // Context
  const { listFocused, listIndex } = useContext(ListContext);
  const { effectiveIndex } = useContext(MenuContext);

  // States
  const [hovered, setHovered] = useState(false);

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  const onPointerEnter = useCallback(() => {
    if (!display) return;
    setHovered(true);
  }, [display]);

  const onPointerLeave = useCallback(() => {
    if (!display) return;
    setHovered(false);
  }, [display]);

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  return useMemo(() => {
    const { index, item, onClick } = parentProps;
    const itemSelected =
      index === listIndex && effectiveIndex.current === index;
    const itemFocused = itemSelected && focus === Focus.MENU;
    return {
      callbacks: {
        itemContainer: {
          onClick: () => onClick(index),
          onPointerEnter,
          onPointerLeave,
        },
      },
      props: {
        iconElement: {
          focused: itemSelected || hovered,
          fromMenu: true,
        },
        itemContainer: {
          collapse,
          focused: listFocused && (itemFocused || itemSelected),
        },
      },
      text: !collapse ? TranslationManager.get(item.text) : null,
    };
  }, [
    collapse,
    effectiveIndex,
    enabled,
    focus,
    hovered,
    listFocused,
    listIndex,
    parentProps,
  ]);
}
